import type {UnitSystem} from '@gaia/types';
import {formatDistance} from '@gaia/utils/formatters';
import turfLength from '@turf/length';

export default function getAreaLines(
    area: Area,
    unitSystem: UnitSystem,
    interaction?: InteractionState
): AreaLine[] {
    if (!area.isGeometryLoaded || !area.isGeometryEditable) return [];
    const areaPoints = Object.values(area.points).sort(
        (a, b) => a.properties.order - b.properties.order
    );

    if (areaPoints.length === 0) return [];

    const lines: AreaLine[] = areaPoints.map((point: AreaPoint, index: number) => {
        const nextIndex = index === areaPoints.length - 1 ? 0 : index + 1;

        return createLine(
            point.geometry.coordinates,
            areaPoints[nextIndex].geometry.coordinates,
            {
                localId: area.properties.localId,
                toPointLocalId: areaPoints[nextIndex].properties.localId,
                type: 'normal',
            },
            unitSystem
        );
    });

    // In create mode remove the line between the last and first vertex, and add two additional
    // lines, one from the last vertex to the cursor, and one from the cursor to the first vertex
    if (
        interaction &&
        interaction.type === 'editingArea' &&
        interaction.subtype === 'appendingPoint' &&
        interaction.mode === 'create' &&
        interaction.cursorLngLat
    ) {
        lines.pop();

        lines.push(
            createLine(
                areaPoints[areaPoints.length - 1].geometry.coordinates,
                interaction.cursorLngLat.toArray(),
                {
                    localId: area.properties.localId,
                    toPointLocalId: undefined,
                    type: 'appendPreview',
                },
                unitSystem
            ),
            createLine(
                interaction.cursorLngLat.toArray(),
                areaPoints[0].geometry.coordinates,
                {
                    localId: area.properties.localId,
                    toPointLocalId: areaPoints[0].properties.localId,
                    type: 'appendPreview',
                },
                unitSystem
            )
        );
    }

    return lines;
}

function createLine(
    coordinate1: GeoJSON.Position,
    coordinate2: GeoJSON.Position,
    properties: Omit<AreaLine['properties'], 'length'>,
    unitSystem: UnitSystem
): AreaLine {
    const geometry: GeoJSON.LineString = {
        type: 'LineString',
        coordinates: [coordinate1, coordinate2],
    };
    const length = formatDistance(
        turfLength({type: 'Feature', geometry, properties: {}}, {units: 'meters'}),
        unitSystem
    );

    return {
        type: 'Feature',
        properties: {length, ...properties},
        geometry,
    };
}
